import { ForeshadowDebtContract } from './ForeshadowDebtContract'
import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, CircleDollarSign, Loader2, Play, Save, Sparkles, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { chapterApi, type GenerateChapterCreditEstimate, type GenerateChapterRequest, type ChapterGenerateLang, type ChapterGenerateMode } from '@/api/chapterApi'
import { projectApi } from '@/api/projectApi'
import { applyChapterDraft, buildChapterDraftAssistInput } from '@/components/chapterDraft'
import { ApiError, friendlyMessage } from '@/api/http'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { JOB_CHAPTER_MODES } from '@/constants/chapterModes'
import { useUiStore, type GeneratingChapter } from '@/stores/uiStore'
import type { Chapter, ChapterOutline } from '@/types/nf'
import { getNextChapterNumber } from '@/utils/chapterGenerate'
import { CONTENT_LANGUAGES } from '@/lib/contentLanguages'
import { formatCredits } from '@/utils/format'

interface ChapterGenerateFormProps {
  projectId: string
  branchId: string
  chapters: Chapter[]
  draft: GeneratingChapter | null
  /** 项目作品语言；仅项目未设置语言时允许本章选择。 */
  contentLanguage?: string
}

function listToText(values: string[] | undefined) {
  return (values ?? []).join('\n')
}

function textToList(value: string) {
  return value
    .split(/\n|,/)
    .map((item) => item.trim())
    .filter(Boolean)
}

function composeConfirmedOutline(summary: string, sceneBeats: string, keyEvents: string, characters: string) {
  return [
    summary.trim(),
    `场景节拍：${textToList(sceneBeats).join('；')}`,
    `关键事件：${textToList(keyEvents).join('；')}`,
    `出场角色：${textToList(characters).join('、')}`,
  ]
    .filter((line) => line.trim() && !line.endsWith('：'))
    .join('\n')
}

function estimateFromError(error: unknown): GenerateChapterCreditEstimate | null {
  if (!(error instanceof ApiError) || !error.data || typeof error.data !== 'object') return null
  const data = error.data as Partial<GenerateChapterCreditEstimate>
  if (typeof data.estimated_credits !== 'number' || typeof data.available_credits !== 'number') return null
  return {
    user_id: typeof data.user_id === 'string' ? data.user_id : '',
    project_id: typeof data.project_id === 'string' ? data.project_id : '',
    estimated_credits: data.estimated_credits,
    balance_credits: typeof data.balance_credits === 'number' ? data.balance_credits : data.available_credits,
    reserved_credits: typeof data.reserved_credits === 'number' ? data.reserved_credits : 0,
    available_credits: data.available_credits,
    sufficient: Boolean(data.sufficient),
  }
}

export function chapterOutlineLoadError(error: unknown, fallback: string): string | null {
  if (error instanceof ApiError && error.status === 404 && error.code === 'not_found') return null
  return friendlyMessage(error, fallback)
}

export function ChapterGenerateForm({ projectId, branchId, chapters, draft, contentLanguage }: ChapterGenerateFormProps) {
  const { t, i18n } = useTranslation()
  const locale = useUiStore((s) => s.locale)
  const contentMode = useUiStore((s) => s.contentMode)
  const startGeneratingChapter = useUiStore((s) => s.startGeneratingChapter)
  const clearGeneratingChapter = useUiStore((s) => s.clearGeneratingChapter)
  const [drafting, setDrafting] = useState(false)
  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectApi.get(projectId),
  })
  const [chapterNumber, setChapterNumber] = useState(draft?.chapterNumber ?? getNextChapterNumber(chapters))
  const [title, setTitle] = useState(draft?.title ?? '')
  const [outline, setOutline] = useState(draft?.outline ?? '')
  const [targetWords, setTargetWords] = useState(draft?.targetWords ?? 0)
  const [chapterMode, setChapterMode] = useState<ChapterGenerateMode>(draft?.chapterMode ?? 'regular')
  const [fallbackLang, setLang] = useState<ChapterGenerateLang>(draft?.lang ?? locale)
  const projectLanguage = projectQuery.data?.content_language?.trim() || contentLanguage?.trim()
  const lang = projectLanguage || fallbackLang
  const [outlineTouched, setOutlineTouched] = useState(false)
  const [outlineDraft, setOutlineDraft] = useState<ChapterOutline | null>(null)
  const [sceneBeatsText, setSceneBeatsText] = useState('')
  const [keyEventsText, setKeyEventsText] = useState('')
  const [charactersText, setCharactersText] = useState('')
  const [outlineDirty, setOutlineDirty] = useState(false)
  const [outlineLoading, setOutlineLoading] = useState(false)
  const [outlineSaving, setOutlineSaving] = useState(false)
  const [outlineError, setOutlineError] = useState<string | null>(null)
  const [creditEstimate, setCreditEstimate] = useState<GenerateChapterCreditEstimate | null>(null)
  const [estimateLoading, setEstimateLoading] = useState(false)
  const [estimateError, setEstimateError] = useState<string | null>(null)

  const [defaultsLoading, setDefaultsLoading] = useState(!draft)
  const [defaultsError, setDefaultsError] = useState<string | null>(null)
  useEffect(() => {
    if (draft) { setDefaultsLoading(false); return }
    let cancelled = false
    setDefaultsLoading(true)
    setDefaultsError(null)
    void chapterApi.nextDefaults(projectId, branchId).then((defaults) => {
      if (cancelled) return
      setChapterNumber(defaults.chapter_no)
      setTargetWords(defaults.target_words)
      setChapterMode(defaults.chapter_mode)
      setTitle(defaults.outline_draft?.title ?? '')
    }).catch((error) => {
      if (!cancelled) setDefaultsError(friendlyMessage(error, t('chapter.generate.defaultsFailed')))
    }).finally(() => { if (!cancelled) setDefaultsLoading(false) })
    return () => { cancelled = true }
  }, [projectId, branchId, draft, t])


  useEffect(() => {
    if (!projectId || !chapterNumber) return
    let cancelled = false
    setOutlineLoading(true)
    setOutlineError(null)
    setOutlineDraft(null)
    setOutlineDirty(false)
    void chapterApi
      .getOutline(projectId, chapterNumber, branchId)
      .then((data) => {
        if (cancelled) return
        setOutlineDraft(data)
        setOutline(data.summary)
        if (!draft?.title) setTitle(data.title ?? '')
        setSceneBeatsText(listToText(data.scene_beats))
        setKeyEventsText(listToText(data.key_events))
        setCharactersText(listToText(data.characters))
        setOutlineDirty(false)
        setOutlineTouched(false)
      })
      .catch((err) => {
        if (cancelled) return
        setOutlineDraft(null)
        setSceneBeatsText('')
        setKeyEventsText('')
        setCharactersText('')
        setOutlineDirty(false)
        const loadError = chapterOutlineLoadError(err, t('chapter.generate.outlineLoadFailed'))
        if (loadError === null) {
          setOutline('')
          setOutlineTouched(false)
          return
        }
        setOutlineError(loadError)
      })
      .finally(() => {
        if (!cancelled) setOutlineLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [branchId, chapterNumber, projectId, t])

  const trimmedOutline = outline.trim()
  const outlineInvalid = outlineTouched && !trimmedOutline
  const outlineConfirmed = outlineDraft?.status === 'confirmed' && !outlineDirty
  const composedOutline = useMemo(
    () => composeConfirmedOutline(outline, sceneBeatsText, keyEventsText, charactersText),
    [charactersText, keyEventsText, outline, sceneBeatsText],
  )
  const generateBody = useMemo<GenerateChapterRequest>(() => ({
    project_id: projectId,
    chapter_number: chapterNumber,
    outline: composedOutline || trimmedOutline,
    title: title.trim() || undefined,
    chapter_mode: chapterMode,
    target_words: targetWords,
    lang,
  }), [chapterMode, chapterNumber, composedOutline, lang, projectId, targetWords, title, trimmedOutline])

  useEffect(() => {
    setCreditEstimate(null)
    setEstimateError(null)
    if (!projectId || !trimmedOutline || !outlineConfirmed) return
    let cancelled = false
    setEstimateLoading(true)
    const timer = window.setTimeout(() => {
      void chapterApi
        .estimateGenerate(generateBody)
        .then((estimate) => {
          if (cancelled) return
          setCreditEstimate(estimate)
          setEstimateError(null)
        })
        .catch((err) => {
          if (cancelled) return
          const estimate = estimateFromError(err)
          setCreditEstimate(estimate)
          setEstimateError(friendlyMessage(err, t('chapter.generate.estimateFailed')))
        })
        .finally(() => {
          if (!cancelled) setEstimateLoading(false)
        })
    }, 300)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [generateBody, outlineConfirmed, projectId, t, trimmedOutline])

  const refreshEstimate = async (bodyOverride?: GenerateChapterRequest) => {
    setEstimateLoading(true)
    setEstimateError(null)
    try {
      const estimate = await chapterApi.estimateGenerate(bodyOverride ?? generateBody)
      setCreditEstimate(estimate)
      return estimate
    } catch (err) {
      const estimate = estimateFromError(err)
      setCreditEstimate(estimate)
      setEstimateError(friendlyMessage(err, t('chapter.generate.estimateFailed')))
      return estimate
    } finally {
      setEstimateLoading(false)
    }
  }

  const markOutlineDirty = () => {
    setOutlineDirty(true)
    setOutlineDraft((prev) => (prev ? { ...prev, status: 'draft' } : prev))
  }

  // summaryOverride: Easy 模式刚拿到草案就要保存, 此时 setOutline 的 state 更新尚未生效,
  // 直接读 outline 会拿到旧值(空串) -> 必须由调用方把新值透传进来。
  const saveOutline = async (summaryOverride?: string) => {
    const summary = (summaryOverride ?? outline).trim()
    if (!summary) {
      setOutlineTouched(true)
      return null
    }
    setOutlineSaving(true)
    setOutlineError(null)
    try {
      const saved = await chapterApi.saveOutline(projectId, chapterNumber, {
        title: title.trim(),
        target_words: targetWords,
        summary,
        scene_beats: textToList(sceneBeatsText),
        key_events: textToList(keyEventsText),
        characters: textToList(charactersText),
        volume_no: outlineDraft?.volume_no,
        branch_id: branchId,
      })
      setOutlineDraft(saved)
      setOutline(saved.summary)
      setSceneBeatsText(listToText(saved.scene_beats))
      setKeyEventsText(listToText(saved.key_events))
      setCharactersText(listToText(saved.characters))
      setOutlineDirty(false)
      toast.success(t('chapter.generate.outlineSaved'))
      return saved
    } catch (err) {
      setOutlineError(friendlyMessage(err, t('chapter.generate.outlineSaveFailed')))
      return null
    } finally {
      setOutlineSaving(false)
    }
  }

  const confirmOutline = async (summaryOverride?: string) => {
    const dirty = outlineDirty || Boolean(summaryOverride)
    const saved = dirty || !outlineDraft ? await saveOutline(summaryOverride) : outlineDraft
    if (!saved) return null
    setOutlineSaving(true)
    setOutlineError(null)
    try {
      const confirmed = await chapterApi.confirmOutline(projectId, chapterNumber, branchId)
      setOutlineDraft(confirmed)
      setOutlineDirty(false)
      toast.success(t('chapter.generate.outlineConfirmed'))
      return confirmed
    } catch (err) {
      setOutlineError(friendlyMessage(err, t('chapter.generate.outlineConfirmFailed')))
      return null
    } finally {
      setOutlineSaving(false)
    }
  }

  // 信用点校验 + 起流式生成。Easy / 高级两条路都走这里, 保证门禁一致。
  const submitGeneration = async (overrides?: { outline?: string; title?: string }) => {
    const finalOutline = (overrides?.outline ?? generateBody.outline).trim()
    if (!finalOutline) return
    // 用最终值现造 body: Easy 链路调用时 outline/title 的 state 更新还没生效,
    // 直接用 generateBody 会把空大纲发去估价 -> 400 -> 误判额度不足而中止。
    const body: GenerateChapterRequest = {
      ...generateBody,
      outline: finalOutline,
      title: (overrides?.title ?? title).trim() || undefined,
    }
    const estimate = await refreshEstimate(body)
    if (!estimate?.sufficient) {
      setEstimateError(t('chapter.generate.insufficientCredits'))
      return
    }
    startGeneratingChapter({
      projectId,
      branchId,
      chapterNumber,
      outline: finalOutline,
      title: (overrides?.title ?? title).trim() || undefined,
      chapterMode,
      targetWords,
      lang,
    })
  }

  // Easy: 一键写下一章。串的是【章节工位内部】的三步 ——
  // 取草案 -> 保存并确认大纲 -> 生成正文, 到产出章节正文即止。
  // 🔴 不跨工位: 剧本/分镜/图/音/视频是否继续, 仍只由项目的
  // cascade_auto_threshold + cascade_stop_at 决定, 本函数不碰这两个设置。
  const startEasy = async () => {
    if (defaultsLoading || defaultsError || drafting || outlineSaving || estimateLoading) return
    const project = projectQuery.data
    if (!project) {
      setOutlineError(t('chapter.generate.easyProjectUnavailable'))
      return
    }
    if (outlineConfirmed && trimmedOutline) {
      await submitGeneration()
      return
    }
    setDrafting(true)
    setOutlineError(null)
    try {
      const response = await projectApi.conceptAssist(buildChapterDraftAssistInput(project, chapters.length))
      if (!response.draft) throw new Error('chapter draft is missing')
      const values = applyChapterDraft({ chapter_number: chapterNumber, title, outline, target_words: targetWords, chapter_mode: chapterMode }, { ...response.draft, title: response.draft.title === project.title ? undefined : response.draft.title })
      const nextOutline = values.outline.trim()
      if (!nextOutline) throw new Error('chapter draft outline is empty')
      setOutline(nextOutline)
      setTitle(values.title ?? '')
      if (values.target_words) setTargetWords(values.target_words)
      if (values.chapter_mode) setChapterMode(values.chapter_mode)
      setOutlineTouched(true)
      const confirmed = await confirmOutline(nextOutline)
      if (!confirmed) return
      await submitGeneration({ outline: nextOutline, title: values.title })
    } catch (err) {
      setOutlineError(friendlyMessage(err, t('chapter.generate.easyDraftFailed')))
    } finally {
      setDrafting(false)
    }
  }

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault()
        setOutlineTouched(true)
        if (defaultsLoading || defaultsError || !trimmedOutline || !outlineConfirmed || estimateLoading) return
        void submitGeneration()
      }}
    >
      {defaultsError ? <p role="alert" className="text-sm text-destructive">{defaultsError}</p> : null}
      <fieldset className="space-y-4" disabled={defaultsLoading || outlineLoading || Boolean(defaultsError)}>
      {contentMode === 'easy' ? (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">{t('chapter.generate.easyHint')}</p>
          <Button
            type="button"
            className="w-full sm:w-auto"
            disabled={drafting || outlineSaving || estimateLoading || !projectQuery.data}
            onClick={() => void startEasy()}
          >
            {drafting || outlineSaving ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
            {drafting ? t('chapter.generate.easyDrafting') : t('chapter.generate.easyStart')}
          </Button>
          {outlineError ? <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{outlineError}</p> : null}
          <Collapsible>
            <CollapsibleTrigger className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">
              {t('chapter.generate.easyDetails')}
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="chapter-number">{t('chapter.generate.chapterNumber')}</Label>
              <Input
                id="chapter-number"
                type="number"
                min={1}
                step={1}
                value={chapterNumber}
                onChange={(event) => setChapterNumber(Math.max(1, Number(event.target.value) || 1))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="chapter-title">{t('chapter.generate.title')}</Label>
              <Input
                id="chapter-title"
                maxLength={120}
                value={title}
                placeholder={t('chapter.generate.titlePlaceholder')}
                onChange={(event) => setTitle(event.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Label htmlFor="chapter-outline">{t('chapter.generate.outline')}</Label>
              <span className={outlineConfirmed ? 'text-xs text-emerald-600 dark:text-emerald-300' : 'text-xs text-muted-foreground'}>
                {outlineLoading
                  ? t('common.loading')
                  : outlineConfirmed
                    ? t('chapter.generate.outlineStatusConfirmed')
                    : t('chapter.generate.outlineStatusDraft')}
              </span>
            </div>
            {outlineError ? <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{outlineError}</p> : null}
            <textarea
              id="chapter-outline"
              value={outline}
              maxLength={4000}
              rows={10}
              className="min-h-56 w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 text-sm leading-6 shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive dark:bg-input/30"
              aria-invalid={outlineInvalid}
              placeholder={t('chapter.generate.outlinePlaceholder')}
              onBlur={() => setOutlineTouched(true)}
              onChange={(event) => {
                setOutline(event.target.value)
                markOutlineDirty()
              }}
            />
            <div className="flex justify-between gap-3 text-xs text-muted-foreground">
              <span className={outlineInvalid ? 'text-destructive' : undefined}>
                {outlineInvalid
                  ? t('chapter.generate.outlineRequired')
                  : outlineConfirmed
                    ? t('chapter.generate.outlineReadyHint')
                    : t('chapter.generate.outlineConfirmHint')}
              </span>
              <span className="nf-mono">{outline.length}/4000</span>
            </div>
          </div>

          <div className="grid gap-3 lg:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="chapter-scene-beats">{t('chapter.generate.sceneBeats')}</Label>
              <textarea
                id="chapter-scene-beats"
                value={sceneBeatsText}
                rows={5}
                className="min-h-28 w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 text-sm leading-6 shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30"
                onChange={(event) => {
                  setSceneBeatsText(event.target.value)
                  markOutlineDirty()
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="chapter-key-events">{t('chapter.generate.keyEvents')}</Label>
              <textarea
                id="chapter-key-events"
                value={keyEventsText}
                rows={5}
                className="min-h-28 w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 text-sm leading-6 shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30"
                onChange={(event) => {
                  setKeyEventsText(event.target.value)
                  markOutlineDirty()
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="chapter-outline-characters">{t('chapter.generate.characters')}</Label>
              <textarea
                id="chapter-outline-characters"
                value={charactersText}
                rows={5}
                className="min-h-28 w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 text-sm leading-6 shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30"
                onChange={(event) => {
                  setCharactersText(event.target.value)
                  markOutlineDirty()
                }}
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="target-words">{t('chapter.generate.targetWords')}</Label>
              <Input
                id="target-words"
                type="number"
                min={100}
                value={targetWords}
                onChange={(event) => setTargetWords(Math.max(100, Number(event.target.value) || 100))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="chapter-mode">{t('chapter.generate.mode')}</Label>
              <select
                id="chapter-mode"
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30"
                value={chapterMode}
                onChange={(event) => setChapterMode(event.target.value as ChapterGenerateMode)}
              >
                {JOB_CHAPTER_MODES.map((mode) => (
                  <option key={mode} value={mode}>
                    {t(`jobChapterMode.${mode}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="chapter-lang">{t('chapter.generate.lang')}</Label>
              <select
                id="chapter-lang"
                disabled={Boolean(projectLanguage)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30"
                value={lang}
                onChange={(event) => setLang(event.target.value)}
              >
                {CONTENT_LANGUAGES.map((option) => (
                  <option key={option.code} value={option.code}>
                    {t(`languageNames.${option.code}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      ) : (
        <>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="chapter-number">{t('chapter.generate.chapterNumber')}</Label>
            <Input
              id="chapter-number"
              type="number"
              min={1}
              step={1}
              value={chapterNumber}
              onChange={(event) => setChapterNumber(Math.max(1, Number(event.target.value) || 1))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="chapter-title">{t('chapter.generate.title')}</Label>
            <Input
              id="chapter-title"
              maxLength={120}
              value={title}
              placeholder={t('chapter.generate.titlePlaceholder')}
              onChange={(event) => setTitle(event.target.value)}
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Label htmlFor="chapter-outline">{t('chapter.generate.outline')}</Label>
            <span className={outlineConfirmed ? 'text-xs text-emerald-600 dark:text-emerald-300' : 'text-xs text-muted-foreground'}>
              {outlineLoading
                ? t('common.loading')
                : outlineConfirmed
                  ? t('chapter.generate.outlineStatusConfirmed')
                  : t('chapter.generate.outlineStatusDraft')}
            </span>
          </div>
          {outlineError ? <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{outlineError}</p> : null}
          <textarea
            id="chapter-outline"
            value={outline}
            maxLength={4000}
            rows={10}
            className="min-h-56 w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 text-sm leading-6 shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive dark:bg-input/30"
            aria-invalid={outlineInvalid}
            placeholder={t('chapter.generate.outlinePlaceholder')}
            onBlur={() => setOutlineTouched(true)}
            onChange={(event) => {
              setOutline(event.target.value)
              markOutlineDirty()
            }}
          />
          <div className="flex justify-between gap-3 text-xs text-muted-foreground">
            <span className={outlineInvalid ? 'text-destructive' : undefined}>
              {outlineInvalid
                ? t('chapter.generate.outlineRequired')
                : outlineConfirmed
                  ? t('chapter.generate.outlineReadyHint')
                  : t('chapter.generate.outlineConfirmHint')}
            </span>
            <span className="nf-mono">{outline.length}/4000</span>
          </div>
        </div>

        <div className="grid gap-3 lg:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="chapter-scene-beats">{t('chapter.generate.sceneBeats')}</Label>
            <textarea
              id="chapter-scene-beats"
              value={sceneBeatsText}
              rows={5}
              className="min-h-28 w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 text-sm leading-6 shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30"
              onChange={(event) => {
                setSceneBeatsText(event.target.value)
                markOutlineDirty()
              }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="chapter-key-events">{t('chapter.generate.keyEvents')}</Label>
            <textarea
              id="chapter-key-events"
              value={keyEventsText}
              rows={5}
              className="min-h-28 w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 text-sm leading-6 shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30"
              onChange={(event) => {
                setKeyEventsText(event.target.value)
                markOutlineDirty()
              }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="chapter-outline-characters">{t('chapter.generate.characters')}</Label>
            <textarea
              id="chapter-outline-characters"
              value={charactersText}
              rows={5}
              className="min-h-28 w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 text-sm leading-6 shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30"
              onChange={(event) => {
                setCharactersText(event.target.value)
                markOutlineDirty()
              }}
            />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="target-words">{t('chapter.generate.targetWords')}</Label>
            <Input
              id="target-words"
              type="number"
              min={100}
              value={targetWords}
              onChange={(event) => setTargetWords(Math.max(100, Number(event.target.value) || 100))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="chapter-mode">{t('chapter.generate.mode')}</Label>
            <select
              id="chapter-mode"
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30"
              value={chapterMode}
              onChange={(event) => setChapterMode(event.target.value as ChapterGenerateMode)}
            >
              {JOB_CHAPTER_MODES.map((mode) => (
                <option key={mode} value={mode}>
                  {t(`jobChapterMode.${mode}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="chapter-lang">{t('chapter.generate.lang')}</Label>
            <select
              id="chapter-lang"
              disabled={Boolean(projectLanguage)}
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30"
              value={lang}
              onChange={(event) => setLang(event.target.value)}
            >
              {CONTENT_LANGUAGES.map((option) => (
                <option key={option.code} value={option.code}>
                  {t(`languageNames.${option.code}`)}
                </option>
              ))}
            </select>
          </div>
        </div>
        </>
      )}

      <div
        className={[
          'rounded-md border px-3 py-2 text-sm',
          creditEstimate && !creditEstimate.sufficient
            ? 'border-destructive/40 bg-destructive/10 text-destructive'
            : 'border-border bg-muted/40 text-muted-foreground',
        ].join(' ')}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="inline-flex items-center gap-2">
            {estimateLoading ? <Loader2 className="size-4 animate-spin" /> : creditEstimate && !creditEstimate.sufficient ? <AlertTriangle className="size-4" /> : <CircleDollarSign className="size-4" />}
            {t('chapter.generate.creditEstimate')}
          </span>
          <span className="nf-mono text-foreground">
            {creditEstimate
              ? t('chapter.generate.creditEstimateValue', {
                estimated: formatCredits(creditEstimate.estimated_credits, i18n.language),
                available: formatCredits(creditEstimate.available_credits, i18n.language),
              })
              : estimateLoading
                ? t('chapter.generate.estimatingCredits')
                : t('chapter.generate.creditEstimateUnavailable')}
          </span>
        </div>
        {estimateError ? <p className="mt-2 text-xs">{estimateError}</p> : null}
      </div>

      {outlineDraft?.foreshadow_contract && <ForeshadowDebtContract projectId={projectId} branchId={branchId} chapterNo={chapterNumber} contract={outlineDraft.foreshadow_contract} onChanged={async () => {setOutlineDraft(await chapterApi.getOutline(projectId,chapterNumber,branchId))}} />}
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="ghost" onClick={clearGeneratingChapter}>
          <X className="size-4" />
          {t('common.cancel')}
        </Button>
        {contentMode === 'advanced' ? (
          <>
            <Button type="button" variant="outline" disabled={outlineSaving || !trimmedOutline} onClick={() => void saveOutline()}>
              {outlineSaving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
              {t('common.save')}
            </Button>
            <Button type="button" variant="outline" disabled={outlineSaving || !trimmedOutline} onClick={() => void confirmOutline()}>
              {outlineSaving ? <Loader2 className="size-4 animate-spin" /> : <CheckCircle2 className="size-4" />}
              {t('chapter.generate.confirmOutline')}
            </Button>
            <Button type="submit" disabled={!trimmedOutline || !outlineConfirmed || estimateLoading || Boolean(creditEstimate && !creditEstimate.sufficient)}>
              {estimateLoading ? <Loader2 className="size-4 animate-spin" /> : <Play className="size-4" />}
              {t('chapter.generate.submit')}
            </Button>
          </>
        ) : null}
      </div>
      </fieldset>
    </form>
  )
}
